<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const insuranceData = ref(null);
const isReadonly = computed(() => props.readonly);
const isVisible = computed(() => props.visible);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isPopupVisible = ref(false);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const searchQuery = ref('');
const filteredOptions = ref([]);

const filterOptions = () => {
    filteredOptions.value = insuranceData.value.filter((option) => option.text.toLowerCase().includes(searchQuery.value.toLowerCase()));
};

onMounted(async () => {
    await Promise.all([searchInsurance()]);
    nextTick(() => {
        filteredOptions.value = insuranceData.value;
    });
});

const searchInsurance = async () => {
    const config = { method: 'get' };
    try {
        const { execute } = useAxios('/api/insurance/col', config, {
            immediate: false,
            onSuccess(response) {
                // console.log('response.data.data!!!!');
                // console.log(response.data.data);

                const newList = response.data.data.map((item) => ({
                    value: item.collision_insurance_state,
                    text: item.insurance_name,
                    id: item.collision_insurance_state,
                    insurance_name: item.insurance_name, // 자차한도 비율
                    collision_coverage_type: item.collision_coverage_type, // 자차한도 비율
                    collision_insurance_age: item.collision_insurance_age, // 자차한도 비율
                    collision_insurance_excess: item.collision_insurance_excess, // 자차면책금금
                    collision_insurance_state: item.collision_insurance_state, //면책종류
                    collision_insurance_coverage: item.collision_insurance_coverage // 면책금액
                }));
                insuranceData.value = newList;
            },
            onError(err) {
                console.error('Error:', err.message);
                insuranceData.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        insuranceData.value = [];
    }
};

watch(
    () => props.modelValue,
    (newVal) => {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    const oldValue = event.target.value;
    const oldLength = oldValue.length;
    let cursorPosition = event.target.selectionStart;
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    const beforeFormatLength = value.length;

    if (props.upper) {
        value = value.toUpperCase();
    }

    const afterFormatLength = value.length;
    const lengthDiff = afterFormatLength - beforeFormatLength;

    cursorPosition = Math.min(cursorPosition, afterFormatLength);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? ValidationMsg.value : validationResult.message;
    emitUpdate(value);

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value,
        vehicle_id: '',
        model: '',
        fuel_type: '',
        mileage: 0
    });
};

const togglePopup = () => {
    isPopupVisible.value = !isPopupVisible.value;
};

const closePopup = () => {
    isPopupVisible.value = false;
};

const selectValue = (option) => {
    inputValue.value = option.value;

    emit('update:modelValue', option.insurance_name);
    emit('input', {
        id: props.inputId,
        value: option.value,
        insurance_name: option.text || '',
        collision_cost: option.collision_cost || '',
        collision_coverage_type: option.collision_coverage_type || '',
        collision_insurance_age: option.collision_insurance_age || '',
        collision_insurance_coverage: option.collision_insurance_coverage || '',
        collision_insurance_state: option.collision_insurance_state || ''
    });


    closePopup();
};

const clearInput = () => {
    emitUpdate('', 'input');
};

watch(isPopupVisible, (newValue) => {
    if (newValue) {
        searchQuery.value = '';
        filterOptions();
    }
});
</script>

<template>
    <!-- 불러오기는 Readonly값일경우에는 표출 하지않도록 한다. -->
    <div
        v-if="!isReadonly && isVisible"
        
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div class="relative flex items-center w-full h-12 justify-center">
                <button @click="togglePopup" class="button-style">자차보험 불러오기</button>
        </div>


        <div v-if="isPopupVisible" class="popup-overlay">
            <div class="popup-content">
                <!-- <h3>선택</h3> -->
                <input v-model="searchQuery" @input="filterOptions" placeholder="자차보험을 검색하세요" class="search-input" style="margin-top: 1rem" />
                <div class="options-container">
                    <table class="driver-grid">
                        <thead>
                            <tr>
                                <th>자차보험명</th>
                                <th>보험가입연령</th>
                                <th>자차한도</th>
                                <th>자차면책금</th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- 콤마 추가 필요 -->
                            <tr v-for="option in filteredOptions" :key="option.value" @click="selectValue(option)" class="popup-item">
                                <td>{{ option.insurance_name }}</td>

                                <td>{{ option.collision_insurance_age }}</td>
                                <td>{{ option.collision_cost }}</td>
                                <td>{{ option.collision_insurance_coverage }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <button @click="closePopup" class="close-button">닫기</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

.text-right {
    text-align: right;
    padding-right: 6rem;
}

.error-border {
    border: 1px solid red !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
}

.button-style_b {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}
.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    transition: color 0.3s ease;
    border-radius: 0.4rem;
    background-color: #F0F0FF;
    color: #3868FF;
    align-items: center;
    position: absolute;
    height: 2.7rem;
    width: 9rem;
    display: flex;
    justify-content: center;
    border: 1px solid #3868FF;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.unit-text {
    font-family: 'SCoreDream500';
    font-size: 0.8rem;
    color: #4b5563;
    height: 100%;
    display: flex;
    align-items: center;
    position: absolute;
    padding-top: 0.2rem;
    right: 2.1rem;
    width: 2rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 1.4rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.popup-content {
    background: #fff;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    text-align: left;
    min-width: 30rem;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 1rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}

.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.popup-content {
    background: #fff;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: auto;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.options-container {
    flex-grow: 1;
    height: 18rem;
    max-height: 30rem;
    overflow-y: auto;
}

.popup-item {
    padding: 0.4rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f0f0f0;
}

.options-container::-webkit-scrollbar {
    width: 0.5rem;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.driver-grid {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.driver-grid th,
.driver-grid td {
    border: 0.1rem solid #ddd;
    border-left: none;
    border-right: none;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    text-align: center;
}

.driver-grid th {
    background-color: #f2f2f2;
}

.driver-grid tr:nth-child(even) {
    background-color: #f9f9f9;
}

.driver-grid th:hover {
    cursor: default;
}

.driver-grid tr:hover {
    background-color: #f5f5f5;
    cursor: pointer;
}
</style>
